import styled, { keyframes } from 'styled-components'

// Animaciones
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const shimmer = keyframes`
  0% { left: -100%; }
  100% { left: 100%; }
`

const progress = keyframes`
  0% { transform: translateX(-100%); }
  50% { transform: translateX(0%); }
  100% { transform: translateX(100%); }
`

// Contenedor principal del loading
export const LoadingContainer = styled.div`
  padding: 20px;
  text-align: center;
  background-color: #f8f9fa;
  border-radius: 12px;
  margin: 15px 0;
  border: 2px solid #e3f2fd;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
`

// Animación de fondo shimmer
export const ShimmerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(33, 150, 243, 0.1), transparent);
  animation: ${shimmer} 2s infinite;
  z-index: 1;
`

// Contenedor del contenido
export const LoadingContent = styled.div`
  position: relative;
  z-index: 2;
`

// Spinner animado
export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e3f2fd;
  border-top: 4px solid #2196f3;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin: 0 auto 15px;
`

// Título principal
export const LoadingTitle = styled.h3`
  color: #1976d2;
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
`

// Mensaje secundario
export const LoadingMessage = styled.p`
  color: #666;
  margin: 0;
  font-size: 14px;
  font-style: italic;
`

// Contenedor de la barra de progreso
export const ProgressContainer = styled.div`
  width: 100%;
  height: 4px;
  background-color: #e3f2fd;
  border-radius: 2px;
  margin-top: 15px;
  overflow: hidden;
`

// Barra de progreso animada
export const ProgressBar = styled.div`
  width: 100%;
  height: 100%;
  background-color: #2196f3;
  border-radius: 2px;
  animation: ${progress} 2s ease-in-out infinite;
`

// Contenedor para items seriados
export const SeriadosContainer = styled.div`
  padding: 10px;
  margin: 10px 0;
  background-color: #e8f5e8;
  border-radius: 4px;
`

// Contenedor para items no seriados
export const NoSeriadosContainer = styled.div`
  padding: 10px;
  margin: 10px 0;
  background-color: #fff3cd;
  border-radius: 4px;
`

// Estilos para el pre (JSON)
export const JsonPre = styled.pre`
  font-size: 12px;
  overflow: auto;
`

// Contenedor principal de la tabla
export const TableContainer = styled.div`
  margin: 20px 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`

// Título de la tabla
export const TableTitle = styled.h4`
  background: #f8f9fa;
  margin: 0;
  padding: 15px 20px;
  border-bottom: 2px solid #e9ecef;
  color: #495057;
  font-size: 16px;
  font-weight: 600;
`

// Tabla principal
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`

// Encabezados de la tabla
export const TableHeader = styled.thead`
  background: #f8f9fa;
`

export const TableHeaderRow = styled.tr`
  border-bottom: 2px solid #dee2e6;
`

export const TableHeaderCell = styled.th`
  padding: 12px 15px;
  text-align: center;
  font-weight: 600;
  color: #495057;
  border-right: 1px solid #dee2e6;
  
  &:last-child {
    border-right: none;
  }
`

// Cuerpo de la tabla
export const TableBody = styled.tbody``

export const TableRow = styled.tr<{ isCompleted?: boolean }>`
  border-bottom: 1px solid #dee2e6;
  background-color: ${props => props.isCompleted ? '#e8f5e8' : 'transparent'};
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: ${props => props.isCompleted ? '#d4f1d4' : '#f8f9fa'};
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  ${props => props.isCompleted && `
    border-left: 4px solid #4caf50;
    box-shadow: 0 2px 4px rgba(76, 175, 80, 0.15);
  `}
`

export const TableCell = styled.td`
  padding: 12px 15px;
  border-right: 1px solid #dee2e6;
  vertical-align: top;
  
  &:last-child {
    border-right: none;
  }
`

// Estilos específicos para cada columna
export const SKUCell = styled(TableCell)`
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #007bff;
  width: 15%;
  text-align: center;
  vertical-align: middle;
`

export const SupplierCodeCell = styled(TableCell)`
  font-family: 'Courier New', monospace;
  width: 15%;
  text-align: center;
  vertical-align: middle;
`

export const DescriptionCell = styled(TableCell)`
  width: 50%;
  line-height: 1.4;
  word-break: break-word;
`

export const QuantityCell = styled(TableCell)`
  text-align: center;
  vertical-align: middle;
  width: 12%;
  font-weight: 600;
`

export const ReceivedCell = styled(TableCell)`
  text-align: center;
  width: 12%;
  position: relative;
`

// Input para cantidad recibida
export const ReceivedInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 8px;
  border: 1px solid ${props => props.hasError ? '#dc3545' : '#6c757d'};
  border-radius: 4px;
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  background-color: #fff;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#dc3545' : '#007bff'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(220, 53, 69, 0.1)' : 'rgba(0, 123, 255, 0.1)'};
  }
  
  &:hover {
    border-color: ${props => props.hasError ? '#c82333' : '#495057'};
  }
  
  &[readonly] {
    background-color: #f8f9fa;
    border-color: #6c757d;
    color: #6c757d;
    cursor: not-allowed;
    
    &:focus {
      border-color: #6c757d;
      box-shadow: none;
    }
    
    &:hover {
      border-color: #6c757d;
    }
  }
`

// Indicador visual para la columna recibido
export const ReceivedIndicator = styled.div<{ isCompleted?: boolean }>`
  position: absolute;
  top: 50%;
  right: -10px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid ${props => props.isCompleted ? '#4caf50' : '#dc3545'};
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
`

// Indicador de completado
export const CompletedIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background-color: #4caf50;
  border-radius: 50%;
  margin-left: 8px;
  
  &::after {
    content: '✓';
    color: white;
    font-size: 12px;
    font-weight: bold;
  }
`

// Contenedor específico para items seriados
export const SeriadosTableContainer = styled(TableContainer)`
  border-left: 4px solid #28a745;
`

export const SeriadosTableTitle = styled(TableTitle)`
  background: #d4edda;
  color: #155724;
  border-bottom-color: #c3e6cb;
`

// Contenedor específico para items no seriados
export const NoSeriadosTableContainer = styled(TableContainer)`
  border-left: 4px solid #ffc107;
`

export const NoSeriadosTableTitle = styled(TableTitle)`
  background: #fff3cd;
  color: #856404;
  border-bottom-color: #ffeaa7;
`